import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MusicIcon, PlayIcon } from 'lucide-react';
import { ActionRail } from './ActionRail';
interface VideoCardProps {
  video: any;
  isActive: boolean;
  onOpenComments: () => void;
  onOpenShare: () => void;
}
export function VideoCard({
  video,
  isActive,
  onOpenComments,
  onOpenShare
}: VideoCardProps) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [showHeart, setShowHeart] = useState(false);
  const clickTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleVideoClick = () => {
    if (clickTimeout.current) {
      // Double click
      clearTimeout(clickTimeout.current);
      clickTimeout.current = null;
      setShowHeart(true);
      setTimeout(() => setShowHeart(false), 1000);
    } else {
      // Single click
      clickTimeout.current = setTimeout(() => {
        setIsPlaying(!isPlaying);
        clickTimeout.current = null;
      }, 250);
    }
  };
  // Parse caption for hashtags
  const renderCaption = (text: string) => {
    return text.split(' ').map((word, i) => {
      if (word.startsWith('#')) {
        return <span key={i} className="font-bold text-white cursor-pointer hover:underline" data-id="element-91">
            {word}{' '}
          </span>;
      }
      return <span key={i} data-id="element-92">{word} </span>;
    });
  };
  return <div className="w-full h-full relative snap-start bg-black overflow-hidden flex-shrink-0" data-id="element-93">
      {/* Fake Video Background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${video.color} opacity-80`} onClick={handleVideoClick} data-id="element-94">
        {/* Animated pattern overlay to make it feel "alive" */}
        <motion.div animate={{
        backgroundPosition: ['0% 0%', '100% 100%']
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
        backgroundImage: 'radial-gradient(circle at center, white 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }} data-id="element-95" />
      </div>

      {/* Play/Pause Overlay */}
      <AnimatePresence data-id="element-96">
        {!isPlaying && <motion.div initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.5
      }} className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-id="element-97">
            <div className="w-20 h-20 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center" data-id="element-98">
              <PlayIcon size={40} className="text-white ml-2" fill="white" data-id="element-99" />
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Double Tap Heart Animation */}
      <AnimatePresence data-id="element-100">
        {showHeart && <motion.div initial={{
        opacity: 0,
        scale: 0.5,
        rotate: -15
      }} animate={{
        opacity: 1,
        scale: 1.5,
        rotate: 0
      }} exit={{
        opacity: 0,
        scale: 2,
        y: -100
      }} transition={{
        duration: 0.5,
        type: 'spring'
      }} className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-id="element-101">
            <div className="text-neonOrange text-9xl drop-shadow-2xl" data-id="element-102">❤️</div>
          </motion.div>}
      </AnimatePresence>

      {/* Bottom Gradient for Text Legibility */}
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" data-id="element-103" />

      {/* Action Rail (Right Side) */}
      <ActionRail user={video.user} likes={video.likes} comments={video.comments} shares={video.shares} isLikedInitial={video.isLiked} onOpenComments={onOpenComments} onOpenShare={onOpenShare} data-id="element-104" />

      {/* Info Overlay (Bottom Left) */}
      <div className="absolute bottom-0 left-0 right-16 p-4 pb-24 z-20 pointer-events-none" data-id="element-105">
        <h3 className="text-white font-bold text-lg mb-2 pointer-events-auto cursor-pointer hover:underline" data-id="element-106">
          @{video.user.username}
        </h3>
        <p className="text-white/90 text-sm mb-4 leading-snug pointer-events-auto" data-id="element-107">
          {renderCaption(video.caption)}
        </p>

        {/* Music Ticker */}
        <div className="flex items-center gap-2 pointer-events-auto bg-black/20 backdrop-blur-sm rounded-full py-1.5 px-3 w-fit max-w-[80%]" data-id="element-108">
          <MusicIcon size={14} className="text-white shrink-0" data-id="element-109" />
          <div className="overflow-hidden whitespace-nowrap relative w-full" data-id="element-110">
            <motion.div animate={{
            x: [0, -200]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'linear'
          }} className="text-white text-xs font-medium inline-block" data-id="element-111">
              {video.music} • {video.music} •
            </motion.div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-[84px] left-0 right-0 h-1 bg-white/20 z-30" data-id="element-112">
        <motion.div className="h-full bg-white/80" initial={{
        width: '0%'
      }} animate={{
        width: isPlaying && isActive ? '100%' : '0%'
      }} transition={{
        duration: 15,
        ease: 'linear',
        repeat: Infinity
      }} data-id="element-113" />
      </div>
    </div>;
}